import Decimal from 'decimal.js'
import { useQuery } from '@tanstack/react-query'
import { Toggle } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { toast } from '@shared/lib/toast/toast-store'
import { formatMoney } from '@shared/lib/format/currency'
import { recuperaAiProductActiveQuery } from '../api/recupera-ai.queries'
import { useSetRecuperaProductActiveMutation } from '../api/recupera-ai.mutations'
import { recuperaCommissionPercent } from '../lib/commission-tiers'
import { desc, note, noteStrong, row, title, wrapper } from './recupera-product-feature.css'

type RecuperaProductFeatureToggleProps = {
  productId: string
  price?: { amount: string; currency: string }
}

export function RecuperaProductFeatureToggle({
  productId,
  price,
}: RecuperaProductFeatureToggleProps) {
  const enabled = useFeatureFlag('recuperaAi')
  const active = useQuery({ ...recuperaAiProductActiveQuery(productId), enabled })
  const setActive = useSetRecuperaProductActiveMutation(productId)

  if (!enabled) {
    return null
  }

  const checked = active.data?.active === true
  const isBrl = price?.currency === 'BRL'
  let ticket: Decimal
  try {
    ticket = price ? new Decimal(price.amount) : new Decimal(Number.NaN)
  } catch {
    ticket = new Decimal(Number.NaN)
  }
  const hasTicket = isBrl && ticket.isFinite() && ticket.gt(0)
  const percent = hasTicket ? recuperaCommissionPercent(ticket.toNumber()) : 0
  const fee = hasTicket
    ? ticket.times(percent).dividedBy(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2)
    : '0.00'

  return (
    <div className={wrapper} data-testid="recupera-ai-product-feature">
      <div className={row}>
        <div>
          <div className={title}>Recupera AI</div>
          <p className={desc}>
            Recupera carrinhos abandonados deste produto por SMS, WhatsApp, e-mail e ligação.
          </p>
        </div>
        <Toggle
          checked={checked}
          disabled={active.isPending || setActive.isPending}
          onChange={(next) =>
            setActive.mutate(next, {
              onError: () => toast.error('Não foi possível atualizar o Recupera AI'),
            })
          }
          ariaLabel="Ativar Recupera AI para este produto"
          data-testid="recupera-ai-product-feature-toggle"
        />
      </div>
      {isBrl && price ? (
        <p className={note} data-testid="recupera-ai-product-feature-commission">
          {hasTicket ? (
            <>
              Baseado no ticket do seu produto ({formatMoney(price.amount, price.currency)}), o
              Recupera AI consome <span className={noteStrong}>{percent}%</span> (
              {formatMoney(fee, price.currency)}) do seu valor por venda recuperada.
            </>
          ) : (
            <>
              O Recupera AI consome uma taxa do seu valor por venda recuperada, conforme o ticket:
              até R$ 300 são <span className={noteStrong}>30%</span>, até R$ 1.000 são 24%, até R$
              5.000 são 18%, até R$ 15.000 são 14% e acima disso 11%.
            </>
          )}
        </p>
      ) : null}
    </div>
  )
}
